import type { Holding } from './api-types'
import { CORE_TOKEN_LOGOS, SOL_MINT, tokenLogo, type DefaultToken } from './default-tokens'
import { NETWORK_LABELS, type Network } from './networks'

/** A token a flow works with: SOL, a holding, a core token or a searched token. */
export type PickedToken = {
  address: string
  symbol: string
  name: string
  decimals?: number
  imageUrl?: string
  priceUsd?: number
  marketCapUsd?: number
  /** balance in whole units when held */
  balance?: number
  isNative: boolean
  /** held in Sonar's vault rather than the wallet */
  custody?: 'vault'
  /** the chain this entry moves on, for a coin the exchanges move on several (ETH): one entry per chain (John, Nov 4 2026) */
  network?: Network
}

/**
 * One entry per chain for a coin the exchanges move on several (John, Nov 4 2026: "remove the separate network selector
 * for ETH, just add 3 ETHs on the dropdowns"): the chain is the entry's name and rides on it. No chains, no entries.
 */
export function perChain(token: PickedToken, networks: readonly Network[]): PickedToken[] {
  return networks.map((network) => ({ ...token, name: NETWORK_LABELS[network], network }))
}

export const SOL_TOKEN: PickedToken = {
  address: SOL_MINT,
  symbol: 'SOL',
  name: 'Solana',
  decimals: 9,
  imageUrl: CORE_TOKEN_LOGOS[SOL_MINT],
  isNative: true,
}

export function holdingToPicked(t: Holding): PickedToken {
  return {
    address: t.isNative ? SOL_TOKEN.address : t.address,
    symbol: (t.symbol ?? '').toUpperCase(),
    name: t.name,
    decimals: t.decimals,
    imageUrl: tokenLogo(t.isNative ? SOL_TOKEN.address : t.address, t.imageUrl),
    priceUsd: t.priceUsd,
    balance: Number(t.balance) / 10 ** t.decimals,
    isNative: t.isNative,
    custody: t.custody,
  }
}

/** A core token listed at zero balance. */
export function defaultToPicked(d: DefaultToken, market?: { priceUsd?: number; marketCapUsd?: number }): PickedToken {
  return {
    address: d.address,
    symbol: d.symbol,
    name: d.name,
    decimals: d.decimals,
    imageUrl: d.imageUrl,
    priceUsd: market?.priceUsd,
    marketCapUsd: market?.marketCapUsd,
    balance: 0,
    isNative: d.address === SOL_MINT,
  }
}
